import React from 'react';
import PropTypes from 'prop-types';
import I18n from 'utils/I18n';
import Avatar from '@strava/ui/Avatar';
import Button, { LinkButton } from '@strava/ui/Button';
import { Markup } from 'interweave';
import { useKudosAndCommentsContext } from '../../../../../shared/KudosCommentsAndAchievements/KudosAndCommentsContext';
import { buildTimeAgoTimestamp } from '../../../../../shared/utils/buildTimeAgoTimeStamp';
import useFeedAnalytics from '../../../../hooks/useFeedAnalytics';
import { DELETE_COMMENT_START } from '../../../../../shared/KudosCommentsAndAchievements/kudosAndComments.actions';
import styles from './styles.scss';
import useFeedIntersectionObserver from '../../../../hooks/useFeedIntersectionObserver';
import CommentReaction from '../../../../../shared/KudosCommentsAndAchievements/CommentReaction';

import Media from '../../../../components/shared/media/Media';
import MediaLeft from '../../../../components/shared/media/MediaLeft';
import MediaBody from '../../../../components/shared/media/MediaBody';

const prefix = 'components.feed.social';

const CommentsThread = ({
  onSeeMoreCommentsClick,
  entityId,
  showCommentReactions
}) => {
  const {
    dispatch,
    kudosAndCommentsEntityState,
    analyticProps
  } = useKudosAndCommentsContext();

  const { inView, intersectionObserverRef } = useFeedIntersectionObserver({
    isParentFeedEntry: false
  });

  useFeedAnalytics({
    ...analyticProps,
    element: 'comment_preview',
    inView
  });

  const commentReactionAnalytics = {
    category: 'feed',
    page: 'activity',
    buttonElement: 'like_comment_btn_on_feed',
    listElement: 'like_list_btn_on_feed'
  };

  const comments = kudosAndCommentsEntityState.comments[entityId] || [];
  const numComments = comments.length;
  const totalComments = Math.min(numComments, 2);

  const deleteComment = (comment) => {
    dispatch({
      type: DELETE_COMMENT_START,
      payload: { comment, entityId, element: 'comments_thread' }
    });
  };

  return (
    <div className={styles.comments} data-testid="comments_thread_container">
      {numComments > 0 && (
        <ol>
          {comments
            .slice(Math.max(numComments - totalComments, 0))
            .map((comment) => {
              const timeAgoTimestamp = buildTimeAgoTimestamp(comment.timestamp);
              const { reactions_summary: reactionsSummary } = comment;
              const hasReacted = reactionsSummary?.has_reacted
                ? reactionsSummary.has_reacted
                : false;
              const commentReactionCount = reactionsSummary?.reaction_count
                ? reactionsSummary.reaction_count
                : 0;

              return (
                <li ref={intersectionObserverRef} key={comment.comment_id}>
                  <Media>
                    <MediaLeft className={styles.mediaLeft}>
                      <Avatar
                        name={comment.athlete.name}
                        src={comment.athlete.avatar_url}
                        type="athlete"
                        size="xsmall"
                        data-testid="comment_avatar"
                        href={comment.athlete.url}
                        badge={comment.athlete.member_type}
                        onClick={() => {
                          kudosAndCommentsEntityState.trackAnalytics({
                            action: 'click',
                            element: 'profile_picture',
                            properties: { element_source: 'comments_thread' }
                          });
                        }}
                      />
                    </MediaLeft>
                    <MediaBody className={styles.comment}>
                      <div className={styles.nameAndTime}>
                        <div className={styles.athleteName}>
                          <a href={comment.athlete.url}>
                            {comment.athlete.name}
                          </a>
                        </div>
                        <div
                          className={styles.timestampAndCommentActions}
                          data-testid="comment-actions"
                        >
                          <Markup noWrap={true} content={timeAgoTimestamp} />
                          {(comment.deletable || comment.reportable) && (
                            <div className={styles.commentActions}>
                              {comment.reportable && (
                                <LinkButton
                                  variant="text"
                                  data-testid="report-comment-btn"
                                  className={styles.reportAndDeleteButtons}
                                  href={`/comments/${comment.comment_id}/feedback`}
                                >
                                  {I18n.t(`${prefix}.report`)}
                                </LinkButton>
                              )}
                              {comment.deletable && (
                                <Button
                                  className={styles.reportAndDeleteButtons}
                                  variant="text"
                                  data-testid="delete-comment-btn"
                                  onClick={() => deleteComment(comment)}
                                  type="button"
                                >
                                  {I18n.t(`${prefix}.delete`)}
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <span className={styles.commentText}>
                        <Markup noWrap={true} content={comment.comment} />
                      </span>
                      {showCommentReactions && (
                        <CommentReaction
                          hasReacted={hasReacted}
                          commentReactionCount={commentReactionCount}
                          commentId={comment.comment_id}
                          entityId={entityId}
                          analyticsProps={commentReactionAnalytics}
                        />
                      )}
                    </MediaBody>
                  </Media>
                </li>
              );
            })}
        </ol>
      )}
      {numComments > 2 && (
        <Button
          data-testid="see_all_comments_btn"
          className={styles.moreCommentsButton}
          variant="text"
          onClick={onSeeMoreCommentsClick}
          type="button"
        >
          {I18n.t(`${prefix}.see_all_comments`, {
            number: numComments
          })}
        </Button>
      )}
    </div>
  );
};

CommentsThread.propTypes = {
  onSeeMoreCommentsClick: PropTypes.func.isRequired,
  entityId: PropTypes.string.isRequired,
  showCommentReactions: PropTypes.bool.isRequired
};

export default CommentsThread;
